import React, { FormEvent, useCallback, useEffect, useRef } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { Button, Delete } from "@formant/ui-kit";

import { varColor } from "../../utils/themeHelpers";
import { Icon } from "../common/Icons/Icon";
import { typographyCoreStyles, typographyStyles } from "../common/Typography";
import { byDisplayType, IDisplayTypeProps } from "./IDisplayTypeProps";
import { useDisplayType, useIsDisplayType } from "./PickerDisplayContext";

const FullSearchWrapper = styled.div<{ NoInlinePadding?: boolean }>`
    display: flex;
    align-items: center;
    padding-inline: ${({ NoInlinePadding }) =>
        NoInlinePadding ? "0" : `${24 / 16}rem ${18 / 16}rem`};
    padding-block: ${19 / 16}rem ${16 / 16}rem;
    gap: ${5 / 16}rem;
    .button--icon {
        svg {
            fill: var(--color-text-primary) !important;
        }
    }
`;

const SmallSearchWrapper = styled.div`
    display: flex;
    align-items: center;
    padding-block: 0.75rem;
    padding-inline: 0.5rem;
    gap: ${5 / 16}rem;
`;

const Input = styled.input<IDisplayTypeProps>(
    typographyCoreStyles,
    props =>
        byDisplayType({
            popover: typographyStyles.body,
            dropdown: typographyStyles.body,
            small: typographyStyles.list,
        })(props),
    props => ({
        lineHeight: `${byDisplayType({
            popover: 27 / 16,
            dropdown: 27 / 16,
            small: 20 / 16,
        })(props)}rem`,
    }),
    css`
        flex-grow: 1;
        appearance: none;
        border: 0;
        margin: 0;
        padding: 0;
        background: inherit;
        font-size: 1rem;
        font-weight: 400;
        color: ${varColor("primary", "white")};

        &::placeholder {
            font-weight: 300;
            color: var(--formant-color-primary-steel03);
            opacity: 0.6;
        }
    `,
);

export interface ISearchFieldProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    id?: string;
    className?: string;
    NoInlinePadding?: boolean;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function SearchField(props: ISearchFieldProps) {
    const { value, onChange, placeholder, className, NoInlinePadding } = props;
    const isEmpty = value === "";

    const inputRef = useRef<HTMLInputElement>(null);
    const clearInput = useCallback(() => {
        onChange("");
        inputRef.current?.focus();
    }, [onChange]);

    const handleInput = useCallback(
        (e: FormEvent<HTMLInputElement>) => {
            onChange(e.currentTarget.value);
        },
        [onChange],
    );

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const SearchWrapper = NoInlinePadding
        ? FullSearchWrapper
        : SmallSearchWrapper;

    return (
        <SearchWrapper className={className} NoInlinePadding={NoInlinePadding}>
            {!useIsDisplayType("small") && (
                <Icon
                    name="icon-search"
                    size="18"
                    color="var(--formant-color-primary-silver)"
                />
            )}
            <Input
                ref={inputRef}
                value={value}
                autoComplete="off"
                onInput={handleInput}
                id={props.id}
                placeholder={placeholder}
                onKeyDown={props.onKeyDown}
                displayType={useDisplayType()}
                data-testid="search-bar"
            />
            <Button
                size="icon"
                variant="ghost"
                disabled={isEmpty}
                onClick={clearInput}
                style={{
                    opacity: isEmpty ? 0 : 1,
                    pointerEvents: isEmpty ? "none" : "auto",
                    transition: "opacity 0.3s ease",
                }}
            >
                <Delete size={18} />
            </Button>
        </SearchWrapper>
    );
}
